import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { CKEditorModule } from 'ckeditor4-angular';
import { ModalID } from '../../models/constants';
import { Subscription } from 'rxjs';
import { SharedService } from '../../services/shared.service';
import { Email } from '../../models/general';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { EmailService } from 'src/app/services/email.service';

@Component({
  selector: 'app-email',
  standalone: true,
  templateUrl: './email.component.html',
  styleUrl: './email.component.scss',
  imports: [ReactiveFormsModule, NgSelectModule, CKEditorModule],
})
export class EmailComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();
  to_values: { name: string; email: string }[] = [];
  cc_values: { name: string; email: string }[] = [];
  bcc_values: { name: string; email: string }[] = [];
  show_bcc: boolean = false;
  page: boolean = false;
  default_email_values: Email = new Email();
  email_type: string = '';
  emailForm: FormGroup;
  selectedFiles: { name: string, content: string, contentType: string }[] = [];
  /** Pre-loaded attachments passed via modalService initialState */
  initialAttachments: { name: string; content: string; contentType: string }[] = [];
  /** Set when opening for a Form 3310.4 email — used to clean up the pending record after send. */
  form4473_id: string;
  pending3310_id: string;
  public editorUrl = 'https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js';
  public editorConfig = {
    allowedContent: true,
    extraPlugins: 'colorbutton',
    removePlugins: 'exportpdf,cloudservices,easyimage',
    startupShowBorders: false,
    height: 300,
    versionCheck: false,
    on: {
      instanceReady: function (evt: any) {
        const editor = evt.editor;
        const head = editor.document.getHead();
        const style = editor.document.createElement('style');
        style.appendText('body { background-color: #1e1e1e !important; color: #e0e0e0 !important; font-family: Arial, sans-serif !important; }');
        head.append(style);
      }
    }
  };

  constructor(
    private fb: FormBuilder,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    public emailService: EmailService
  ) {
    this.emailForm = this.fb.group({
      to: ['', Validators.required],
      cc: [''],
      bcc: [''],
      subject: ['', Validators.required],
      body: [''],
      message: [''],
    });
  }
  ngOnInit(): void {
    if (this.email_type === 'ffl_request') {
      for (const contact of this.accountingService.vendors) {
        if (contact.ffl.ffl?.length > 0 && contact.contact.email?.length > 0) {
          if (!this.to_values.some(v => v.email === contact.contact.email)) {
            const entry = { name: contact.contact.display_name, email: contact.contact.email };
            this.to_values.push(entry);
            this.cc_values.push(entry);
            this.bcc_values.push(entry);
          }
        }
      }
    }
    this.attachFfl();
    this.attachSot();
    // Merge any pre-loaded attachments passed via initialState (e.g. Form 3310 PDF)
    if (this.initialAttachments?.length) {
      this.selectedFiles.push(...this.initialAttachments);
    }
    const companyDefaultsMap = this.sharedService.company_info.email_templates;
    const typeDefaults = companyDefaultsMap[this.email_type] || companyDefaultsMap['other'];

    const footerHtml = (typeDefaults?.include_footer) ? this.getEmailFooter() : '';

    if (this.default_email_values) {
      const { to, cc, bcc } = this.default_email_values;

      // ng-select needs objects that match bindLabel and bindValue
      if (to && !this.to_values.some(v => v.email === to)) this.to_values.push({ name: to, email: to });
      if (cc && !this.cc_values.some(v => v.email === cc)) this.cc_values.push({ name: cc, email: cc });
      if (bcc && !this.bcc_values.some(v => v.email === bcc)) this.bcc_values.push({ name: bcc, email: bcc });

      // patchValue needs arrays for [multiple]="true" fields
      this.emailForm.patchValue({
        ...this.default_email_values,
        to: to ? [to] : [],
        cc: cc ? [cc] : [],
        bcc: bcc ? [bcc] : [],
        subject: this.default_email_values.subject || typeDefaults?.subject || '',
        body: (this.default_email_values.body || typeDefaults?.body || '').replace(/\n/g, '<br>') + footerHtml
      });
    } else {
      this.emailForm.patchValue({
        subject: typeDefaults?.subject || '',
        body: (typeDefaults?.body || '').replace(/\n/g, '<br>') + footerHtml
      });
    }
  }

  getEmailFooter() {
    const sender = `${this.sharedService.logged_in_user.name_first} ${this.sharedService.logged_in_user.name_last}`;
    const employee_position = this.sharedService.logged_in_user.position_title;
    const employee_email = this.sharedService.logged_in_user.email? this.sharedService.logged_in_user.email : this.sharedService.company_info.company_email
    const employee_phone = this.sharedService.logged_in_user.phone? this.sharedService.logged_in_user.phone : this.sharedService.company_info.phone_number;
    const company_logo = this.sharedService.company_info.company_logo;
    const address = `${this.sharedService.company_info.address}, ${this.sharedService.company_info.city}, ${this.sharedService.company_info.state} ${this.sharedService.company_info.zip_code}`;
    const website = this.sharedService.company_info.website;
    return `
      <br><br>
      <table border="0" cellpadding="0" cellspacing="0" style="border: none; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; line-height: 1.5; border-collapse: collapse;">
        <tr>
          <!-- Logo Column -->
          <td style="padding: 10px 25px 10px 0; border-right: 1px solid #d4d4d4; vertical-align: middle;">
            <img src="${company_logo}" width="150" style="display: block; width: 150px; height: auto;" alt="Boomstick Defense Logo">
          </td>
          <!-- Info Column -->
          <td style="padding: 10px 0 10px 25px; vertical-align: middle;">
            <div style="font-size: 18px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; color: white; margin-bottom: 2px;">${sender}</div>
            <div style="font-size: 13px; font-style: italic; color: #d4d4d4; margin-bottom: 12px;">${employee_position}</div>
            
            <div style="font-size: 13px; color: #d4d4d4; margin-bottom: 4px;">
              <strong style="color: #d4d4d4;">Direct:</strong> ${employee_phone}
            </div>
            <div style="font-size: 13px; color: #d4d4d4; margin-bottom: 4px;">
              <strong style="color: #d4d4d4;">Email:</strong> <a href="mailto:${employee_email}" style="color: #0078d4; text-decoration: none;">${employee_email}</a>
            </div>
            <div style="font-size: 13px; color: #d4d4d4; margin-bottom: 4px;">
              <strong style="color: #d4d4d4;">Address:</strong> ${address}
            </div>
            <div style="font-size: 13px; color: #d4d4d4;">
              <strong style="color: #d4d4d4;">Web:</strong> <a href="${website}" style="color: #0078d4; text-decoration: none;">${website}</a>
            </div>
          </td>
        </tr>
      </table>
    `;
  }
  async attachFfl() {
    if (this.sharedService.company_info.email_templates[this.email_type]?.include_ffl) {
      if (!this.sharedService.company_info.ffl_document && this.sharedService.company_info.company_id) {
        const result = await this.sharedService.getFFLDocument(this.sharedService.company_info.company_id);
        this.sharedService.company_info.ffl_document = result.document;
      }
      if (this.sharedService.company_info.ffl_document) {
        this.selectedFiles.push({ name: 'FFL.pdf', content: this.sharedService.company_info.ffl_document, contentType: 'application/pdf' });
      }
    }
  }
  async attachSot() {
    if (this.sharedService.company_info.email_templates[this.email_type]?.include_sot) {
      if (!this.sharedService.company_info.sot_document && this.sharedService.company_info.company_id) {
        const result = await this.sharedService.getSOTDocument(this.sharedService.company_info.company_id);
        this.sharedService.company_info.sot_document = result.document;
      }
      if (this.sharedService.company_info.sot_document) {
        this.selectedFiles.push({ name: 'SOT.pdf', content: this.sharedService.company_info.sot_document, contentType: 'application/pdf' });
      }
    }
  }

  previewFile(file: { name: string, content: string, contentType: string }) {
    const newWindow = window.open();
    if (newWindow) {
      const content = file.content ?? '';
      const isSafeUrl =
        content.startsWith('data:application/pdf') ||
        content.startsWith('blob:');
      if (!isSafeUrl) {
        newWindow.close();
        return;
      }
      newWindow.document.title = file.name;
      const body = newWindow.document.body;
      body.style.cssText =
        'margin:0;display:flex;justify-content:center;align-items:center;height:100vh;background:#1e1e1e;';
      const iframe = newWindow.document.createElement('iframe');
      iframe.src = content;
      iframe.style.cssText = 'width:100%;height:100%;border:none;';
      body.appendChild(iframe);
    }
  }

  onSubmit() {
    if (this.emailForm.valid) {
      const to = this.emailForm.value.to[0];
      const subject = this.emailForm.value.subject;
      const html = this.emailForm.value.body;
      const logoBase64 = this.sharedService.company_info.company_logo;

      const companyDefaultsMap = this.sharedService.company_info.email_templates;
      const typeDefaults = companyDefaultsMap[this.email_type] || companyDefaultsMap['other'];
      let attachments: any[] = []; // Allow any type for mix of legacy and new

      // Add user selected files
      this.selectedFiles.forEach(file => {
        attachments.push({ filename: file.name, content: file.content, encoding: 'base64', contentType: file.contentType });
      });
      if (typeDefaults) {
        if (typeDefaults.include_ffl && this.sharedService.company_info.ffl_document) {
          attachments.push({ filename: 'FFL.pdf', path: this.sharedService.company_info.ffl_document });
        }
        if (typeDefaults.include_sot && this.sharedService.company_info.sot_document) {
          attachments.push({ filename: 'SOT.pdf', path: this.sharedService.company_info.sot_document });
        }
      }

      this.sharedService.closeModal(ModalID.email);
      this.subscriptions.add(
        this.emailService.sendEmail({ to, subject, html, logoBase64, attachments }).subscribe(response => {
          this.sharedService.raiseSuccessToast('Email successfully sent');
          // Notify subscribers (e.g. bound book) so they can clean up pending 3310 records
          this.sharedService.email_sent$.next({
            email_type: this.email_type,
            form4473_id: this.form4473_id,
            pending3310_id: this.pending3310_id,
          });
        })
      );
    } else {
      this.sharedService.raiseErrorToast('Please fill out all required fields');
    }
  }

  closeModalClick() {
    this.sharedService.closeModal(ModalID.email);
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.selectedFiles.push({
            name: file.name,
            content: e.target.result,
            contentType: file.type
          });
        };
        reader.readAsDataURL(file);
      }
    }
    // Clear input so same file can be selected again if needed (though we append)
    event.target.value = '';
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
